import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { JwtService } from '@core/services/jwt.service';
import { inject } from '@angular/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';
import { AuthService } from '@modules/auth/auth.service';
import { DEFAULT_REFRESH_URL_BLACKLIST } from '@core/constants/app.constants';
import { ApiResponse } from '@core/interfaces/apiResponse.interfaces';
import { TenantSessionService } from '@core/services/tenant-session.service';
import { AlertService } from '@core/services/alert.service';

const refreshRequests = new Map<string, Observable<string>>();
let sessionExpirationHandledForToken: string | null = null;
const SESSION_EXPIRED_MESSAGE = 'Tu sesión expiró. Inicia sesión de nuevo.';

function buildSessionExpiredError() {
  return {
    status: 'error',
    message: SESSION_EXPIRED_MESSAGE,
    error: {
      code: 'SESSION_EXPIRED',
      details: {},
    },
  };
}

function extractHttpStatus(error: unknown): number {
  if (error instanceof HttpErrorResponse) {
    return Number(error.status);
  }

  if (!error || typeof error !== 'object') {
    return Number.NaN;
  }

  const candidate = error as {
    status?: unknown;
    code?: unknown;
    error?: {
      status?: unknown;
      code?: unknown;
    };
  };

  const statusCandidates = [
    candidate.status,
    candidate.code,
    candidate.error?.status,
    candidate.error?.code,
  ];

  for (const statusCandidate of statusCandidates) {
    const parsedStatus = Number(statusCandidate);
    if (Number.isFinite(parsedStatus)) {
      return parsedStatus;
    }
  }

  return Number.NaN;
}

function buildAuthHeaders(
  token: string,
  tenantSessionService: TenantSessionService,
  resolvedTenantId?: string | null,
): Record<string, string> {
  const tenantId = resolvedTenantId?.trim() || tenantSessionService.activeTenantId;

  return {
    Authorization: `Bearer ${token}`,
    ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
  };
}

function handleSessionExpired(
  authService: AuthService,
  alertService: AlertService,
  expiredToken: string,
): void {
  if (sessionExpirationHandledForToken === expiredToken) {
    return;
  }

  sessionExpirationHandledForToken = expiredToken;
  alertService.open({ type: 'warning', message: SESSION_EXPIRED_MESSAGE });
  authService.logout();
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(JwtService);
  const authService = inject(AuthService);
  const tenantSessionService = inject(TenantSessionService);
  const alertService = inject(AlertService);

  const excludedUrls: string[] = DEFAULT_REFRESH_URL_BLACKLIST;

  if (excludedUrls.some(url => req.url.includes(url))) {
    return next(req);
  }

  const token = tokenService.getAuthToken();

  if (!token) {
    return next(req);
  }

  const authReq = req.clone({
    setHeaders: buildAuthHeaders(token, tenantSessionService),
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse | ApiResponse<any>) => {
      const status = extractHttpStatus(error);

      if (status !== 401) {
        return throwError(() => error);
      }

      let refreshTokenRequest$ = refreshRequests.get(token);
      if (!refreshTokenRequest$) {
        refreshTokenRequest$ = tokenService.refreshToken(token).pipe(
          map((refreshResult: ApiResponse<any>) => {
            const refreshedToken = String(refreshResult?.data ?? '').trim();

            if (!refreshedToken) {
              throw buildSessionExpiredError();
            }

            tokenService.setAuthToken(refreshedToken);
            sessionExpirationHandledForToken = null;
            return refreshedToken;
          }),
          finalize(() => {
            refreshRequests.delete(token);
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
        refreshRequests.set(token, refreshTokenRequest$);
      }

      return refreshTokenRequest$.pipe(
        switchMap((refreshedToken) => {
          const refreshedTenantId =
            tenantSessionService.resolveTenantIdFromToken(refreshedToken) ??
            tenantSessionService.activeTenantId;
          const refreshedReq = req.clone({
            setHeaders: buildAuthHeaders(
              refreshedToken,
              tenantSessionService,
              refreshedTenantId,
            ),
          });

          return next(refreshedReq);
        }),
        catchError(() => {
          // An old request must not sign out a different session started meanwhile.
          if (tokenService.getAuthToken() === token) {
            handleSessionExpired(authService, alertService, token);
          }
          return throwError(() => buildSessionExpiredError());
        })
      );
    })
  );
};
